import { useEffect, useState } from 'react';
import { Download, ExternalLink } from 'lucide-react';
import { resolveRpEmbed } from '@mlm/shared';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@mlm/ui';
import { openResource, resourceOfficeEmbed, resourceUrl, type MemberResource } from '../lib/api';
import { resourceRenderer } from '../lib/resource-renderer';

/**
 * In-app viewer for a member resource — the "viewable/playable in the members
 * area" half of the Resources library, as opposed to download-only.
 *
 * Which renderer a resource gets is decided by `resourceRenderer`
 * (`lib/resource-renderer.ts`), which both this dialog and the page's button
 * visibility read, so the page can never offer a View button for something the
 * dialog then can't display.
 *
 * Every renderer keeps a Download button. An `<iframe>` load failure is not
 * observable cross-origin — no error event, no readable document — so the
 * fallback is always PRESENT rather than swapped in on a failure we cannot
 * detect.
 */
export function ResourceViewerDialog({
  resource,
  officeViewerOn,
  onClose,
}: {
  /** null = closed. */
  resource: MemberResource | null;
  officeViewerOn: boolean;
  onClose: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const renderer = resource ? resourceRenderer(resource, officeViewerOn) : 'download';

  useEffect(() => {
    if (!resource) {
      setSrc(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setSrc(null);
    setError(null);
    (async () => {
      try {
        if (renderer === 'pdf') {
          // The presigned URL is signed `inline`, so the browser's built-in PDF
          // viewer takes it rather than downloading.
          const { url } = await resourceUrl(resource.id, 'view');
          if (!cancelled) setSrc(url);
        } else if (renderer === 'office') {
          const { embedUrl } = await resourceOfficeEmbed(resource.id);
          if (!cancelled) setSrc(embedUrl);
        }
      } catch {
        if (!cancelled) setError('Preview unavailable.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resource, renderer]);

  if (!resource) return null;

  const embed = renderer === 'link' ? resolveRpEmbed(resource.linkUrl ?? '') : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="pr-8">{resource.title}</DialogTitle>
        </DialogHeader>

        <div className="aspect-video w-full overflow-hidden rounded-md border bg-muted">
          {renderer === 'link' && embed ? (
            embed.kind === 'video' ? (
              <video src={embed.src} controls autoPlay className="h-full w-full" />
            ) : (
              <iframe
                src={embed.src}
                title={resource.title}
                className="h-full w-full"
                // Unioned YouTube + Vimeo embed attributes. Permissions Policy
                // is DELEGATED, not inherited: without `allow` the player
                // silently loses autoplay, fullscreen, PiP and DRM.
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                // Vimeo's "only on domains I choose" check reads this request's
                // referer — pinned so a stricter global policy can't 403 every
                // domain-locked embed.
                referrerPolicy="strict-origin-when-cross-origin"
              />
            )
          ) : error ? (
            <div className="flex h-full w-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
              {error} Download it instead.
            </div>
          ) : src ? (
            <iframe src={src} title={resource.title} className="h-full w-full" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
              Loading preview…
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {renderer === 'office'
              ? 'Displayed by the Microsoft Office viewer.'
              : (resource.originalFilename ?? '')}
          </p>
          <div className="flex gap-2">
            {renderer === 'link' && resource.linkUrl ? (
              <Button size="sm" variant="outline" asChild>
                <a href={resource.linkUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" /> Open in new tab
                </a>
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={downloading}
                onClick={async () => {
                  setDownloading(true);
                  try {
                    await openResource(resource.id, 'download');
                  } finally {
                    setDownloading(false);
                  }
                }}
              >
                <Download className="h-4 w-4" /> {downloading ? 'Preparing…' : 'Download'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
